import api from '~/plugins/apiClient';
import { formatApiUrl } from '~/plugins/apiClient/utils';
import { ApiHandlerWithArgs, ApiHandlerWithData, ApiResponseContext } from '~/plugins/apiClient/_types';
import { transformZodSchemaFn } from '~/plugins/zod/utils';
import { transformFetchListOptionsToQuery } from '~/api/_transformers';
import { FetchListOptions, PaginationMetaSchema } from '~/api/_types';
import {
    CertificateChangeFormData,
    CertificateDetail,
    CertificateDetailSchema,
    CertificatesQueueInfo,
    CertificatesQueueInfoSchema,
    FetchCertificatesResponse,
    FetchCertificatesResponseSchema,
} from './_types';
import { LanguageCode } from '../links/_types';
import { CetificateType } from '~/enums/_types';

const apiEndpointCertPrefix = 'certificates';
const apiEndpointQueuePrefix = 'queue';

export const fetchCertificatesRequest: ApiHandlerWithArgs<
    FetchListOptions,
    ApiResponseContext<FetchCertificatesResponse | null>
> = async (opt) => {
    const url = formatApiUrl(apiEndpointCertPrefix);
    return api.get(url, {
        transform: transformZodSchemaFn(FetchCertificatesResponseSchema, url),
        transformMeta: transformZodSchemaFn(PaginationMetaSchema, url),
        params: transformFetchListOptionsToQuery(opt),
    });
};

export const fetchCertificateDetailRequest: ApiHandlerWithData<number, ApiResponseContext<CertificateDetail>> = async (
    id
) => {
    const url = formatApiUrl(apiEndpointCertPrefix, id);
    return api.get(url, { transform: transformZodSchemaFn(CertificateDetailSchema, url) });
};

export const fetchCertificatesQueueRequest = () => {
    const url = formatApiUrl(apiEndpointQueuePrefix);
    return api.get<CertificatesQueueInfo>(url, {
        transform: (responseContext: CertificatesQueueInfo) =>
            responseContext?.Data === null
                ? null
                : transformZodSchemaFn(CertificatesQueueInfoSchema, url)(responseContext),
    });
};

export const putCertificateQueueReorderRequest = (data: {
    code: LanguageCode;
    type: CetificateType;
    reorder: number[];
}) =>
    api.post(formatApiUrl(apiEndpointQueuePrefix, `${data.code}/${data.type}/reorder`), {
        CertificateIds: data.reorder,
    });

export const postCertificateQueueRequest = (id: number) => api.post(formatApiUrl(apiEndpointCertPrefix, `${id}/queue`));
export const deleteCertificateRequest = (id: number) => api.delete(formatApiUrl(apiEndpointCertPrefix, `${id}`));

export const deleteCertificateQueueRequest: ApiHandlerWithData<number, ApiResponseContext<null>> = async (id) => {
    const url = formatApiUrl(apiEndpointCertPrefix, `${id}/queue`);
    return api.delete(url);
};

export const postCertificateQueuePrintRequest = (code: LanguageCode, type: CetificateType) => {
    const url = formatApiUrl(apiEndpointQueuePrefix, `${code}/${type}/print`);
    return api.post<Blob>(url, undefined, { responseType: 'blob' });
};

export const postQueuePrintRequest: ApiHandlerWithData<LanguageCode, ApiResponseContext<null>> = (code) =>
    api.post(formatApiUrl(apiEndpointQueuePrefix, `${code}/print`));

export const postCertificatePrintRequest: ApiHandlerWithData<number, ApiResponseContext<null>> = (id) => {
    const url = formatApiUrl(apiEndpointCertPrefix, `${id}/print`);
    return api.post(url);
};

export const patchCertificateChangeFormRequest: ApiHandlerWithData<
    { id: number; data: CertificateChangeFormData },
    ApiResponseContext<CertificateDetail | null, CertificateChangeFormData>
> = async ({ id, data }) => {
    const url = formatApiUrl(apiEndpointCertPrefix, `${id}/change`);
    return api.patch(url, data, {
        transform: transformZodSchemaFn(CertificateDetailSchema, url),
    });
};
